// Frontend-side rate limiting for the "短信提醒" button in chat. The
// real cap will be enforced by the backend later; this is UX guidance
// so a user doesn't hammer the button.
//
// Limits per (currentUserId, threadId):
//   * 3 sends per local calendar day (Asia/Shanghai)
//   * 1-hour minimum interval between sends

const STORAGE_PREFIX = 'sms_reminder:';
export const SMS_DAILY_LIMIT = 3;
export const SMS_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

interface QuotaRecord {
    dayKey: string;       // local Asia/Shanghai date "YYYY-MM-DD"
    count: number;        // sends so far today
    lastSentAt: number;   // epoch ms of the most recent send
}

// Today's calendar date in Asia/Shanghai (UTC+8) — independent of the
// browser's local timezone so the cap behaves the same for users
// abroad.
function shanghaiDayKey(now: number): string {
    const utcMs = now + 8 * 60 * 60 * 1000;
    const d = new Date(utcMs);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function key(userId: string, threadId: string): string {
    return `${STORAGE_PREFIX}${userId}:${threadId}`;
}

function read(userId: string, threadId: string): QuotaRecord | null {
    try {
        const raw = localStorage.getItem(key(userId, threadId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.dayKey !== 'string' || typeof parsed?.count !== 'number' || typeof parsed?.lastSentAt !== 'number') {
            return null;
        }
        return parsed as QuotaRecord;
    } catch {
        return null;
    }
}

function write(userId: string, threadId: string, rec: QuotaRecord): void {
    try {
        localStorage.setItem(key(userId, threadId), JSON.stringify(rec));
    } catch {
        // Storage full or disabled; silently ignore — frontend cap is
        // best-effort, server enforces the real limit.
    }
}

export interface QuotaStatus {
    canSend: boolean;
    remainingToday: number;          // 0..SMS_DAILY_LIMIT
    nextAllowedAt: number | null;    // epoch ms when interval cooldown ends, or null if not in cooldown
    reason?: 'daily_limit' | 'interval_cooldown';
}

export function getQuotaStatus(userId: string, threadId: string, now: number = Date.now()): QuotaStatus {
    const today = shanghaiDayKey(now);
    const rec = read(userId, threadId);
    const sameDay = rec?.dayKey === today;
    const count = sameDay ? rec!.count : 0;
    const lastSentAt = sameDay ? rec!.lastSentAt : 0;
    const remainingToday = Math.max(0, SMS_DAILY_LIMIT - count);

    if (remainingToday <= 0) {
        return { canSend: false, remainingToday: 0, nextAllowedAt: null, reason: 'daily_limit' };
    }
    if (lastSentAt && now - lastSentAt < SMS_INTERVAL_MS) {
        return {
            canSend: false,
            remainingToday,
            nextAllowedAt: lastSentAt + SMS_INTERVAL_MS,
            reason: 'interval_cooldown',
        };
    }
    return { canSend: true, remainingToday, nextAllowedAt: null };
}

// Records a successful send. Caller decides when to invoke (i.e. after
// the backend confirms the SMS was queued).
export function recordSend(userId: string, threadId: string, now: number = Date.now()): void {
    const today = shanghaiDayKey(now);
    const rec = read(userId, threadId);
    const sameDay = rec?.dayKey === today;
    const next: QuotaRecord = {
        dayKey: today,
        count: (sameDay ? rec!.count : 0) + 1,
        lastSentAt: now,
    };
    write(userId, threadId, next);
}

export function formatCooldown(nextAllowedAt: number, now: number = Date.now()): string {
    const remainingMs = Math.max(0, nextAllowedAt - now);
    const minutes = Math.ceil(remainingMs / 60_000);
    if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours} 小时 ${mins} 分钟后` : `${hours} 小时后`;
    }
    return `${minutes} 分钟后`;
}
